<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="openCreateModal">
        <md-icon>add</md-icon>
        Add to Whitelist
      </md-button>
      <md-button class="md-info md-round" @click="exportData">
        <md-icon>download</md-icon>
        Export
      </md-button>
    </template>

    <template #content>
      <!-- Filters -->
      <div class="filters-bar">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-30 md-small-size-100">
            <md-field>
              <label>Target Type</label>
              <md-select v-model="filters.target_type" @md-selected="onFilterChange">
                <md-option value="">All Types</md-option>
                <md-option value="station">Station</md-option>
                <md-option value="workplace">Workplace</md-option>
                <md-option value="port">Port</md-option>
                <md-option value="department">Department</md-option>
                <md-option value="labourer">Labourer</md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>Status</label>
              <md-select v-model="filters.status" @md-selected="onFilterChange">
                <md-option value="">All</md-option>
                <md-option value="active">Active</md-option>
                <md-option value="expired">Expired</md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-50 md-small-size-100"></div>
        </div>
      </div>

      <!-- Data Table -->
      <table-data
          ref="table"
          title="Whitelist Entries"
          :columns="columns"
          :data="displayedWhitelist"
          :total="total"
          :loading="isPageLoading"
          loading-text="Loading whitelist..."
          :searchable="true"
          search-placeholder="Search by target name..."
          storage-key="biometric-whitelist"
          empty-message="No whitelist entries found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-primary md-round" @click="openCreateModal">
            <md-icon>add</md-icon>
            Add to Whitelist
          </md-button>
          <md-button class="md-info md-round" @click="exportData">
            <md-icon>download</md-icon>
            Export
          </md-button>
        </template>

        <template #cell-target_type="{ item }">
          <badge :type="getTargetTypeBadge(item.target_type)">
            {{ formatTargetType(item.target_type) }}
          </badge>
        </template>

        <template #cell-target_name="{ item }">
          <strong>{{ item.target_name || 'N/A' }}</strong>
        </template>

        <template #cell-start_date="{ item }">
          {{ formatDate(item.start_date) }}
        </template>

        <template #cell-end_date="{ item }">
          {{ item.end_date ? formatDate(item.end_date) : 'Permanent' }}
        </template>

        <template #cell-is_active="{ item }">
          <badge :type="item.is_active ? 'success' : 'danger'">
            {{ item.is_active ? 'Active' : 'Expired' }}
          </badge>
        </template>

        <template #cell-reason="{ item }">
          <span class="reason-text" :title="item.reason">
            {{ item.reason || '-' }}
          </span>
        </template>

        <template #cell-creator="{ item }">
          {{ item.creator ? getCreatorName(item.creator) : 'System' }}
        </template>

        <template #actions="{ item }">
          <md-button class="md-icon-button md-primary" @click="openEditModal(item)" title="Edit">
            <md-icon>edit</md-icon>
          </md-button>
          <md-button class="md-icon-button md-danger" @click="confirmDelete(item)" title="Delete">
            <md-icon>delete</md-icon>
          </md-button>
        </template>

        <template #empty-actions>
          <md-button class="md-primary md-round" @click="openCreateModal">
            <md-icon>add</md-icon>
            Add your first whitelist entry
          </md-button>
        </template>
      </table-data>
      <!-- Whitelist Form Modal -->
      <whitelist-form-modal
          v-model="modal.active"
          :item="modal.item"
          @success="onModalSuccess"
          @error="onModalError"
          @closed="onModalClosed"
      />
    </template>


  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import TableData from '@/components/DataTable/TableData.vue';
import Badge from '@/components/Badge.vue';
import PageMixin from '@/mixins/page-mixin';
import formMixin from '@/mixins/form-mixin';
import Swal from 'sweetalert2';
import WhitelistFormModal from "@/pages/Biotime/Whitelist/WhitelistFormModal.vue";

export default {
  name: 'BiometricWhitelistPage',
  components: {
    PageLayout,
    TableData,
    Badge,
    WhitelistFormModal
  },
  mixins: [PageMixin, formMixin],
  data() {
    return {
      whitelistData: [],
      total: 0,
      isPageLoading: false,
      filters: {
        target_type: '',
        start_date: '',
        end_date: '',
        status: '',
        currentPage: 1,
        perPage: 10
      },
      searchQuery: '',
      pagination: {
        currentPage: 1,
        perPage: 10
      },
      modal: {
        active: false,
        item: null // null = create, object = edit
      },
      columns: [
        { field: 'target_type', label: 'Target Type' },
        { field: 'target_name', label: 'Target Name' },
        { field: 'start_date', label: 'Start Date' },
        { field: 'end_date', label: 'End Date' },
        { field: 'is_active', label: 'Status' },
        { field: 'reason', label: 'Reason' },
        { field: 'creator', label: 'Created By' }
      ]
    };
  },
  computed: {
    pageTitle() {
      return 'Biometric Whitelist';
    },
    pageBreadcrumb() {
      return [
        { label: 'Settings', path: '/settings' },
        { label: 'Biometric Whitelist' }
      ];
    },
    pageInfoCards() {
      var total = this.whitelistData.length;
      var active = 0;
      var expired = 0;
      var labourers = 0;

      this.whitelistData.forEach(function (item) {
        if (item.is_active) active++;
        else expired++;
        if (item.target_type === 'labourer') labourers++;
      });

      return [
        { icon: 'list_alt', label: 'Total Entries', value: total, color: 'primary' },
        { icon: 'check_circle', label: 'Active', value: active, color: 'success' },
        { icon: 'schedule', label: 'Expired', value: expired, color: 'warning' },
        { icon: 'people', label: 'Labourer Entries', value: labourers, color: 'info' }
      ];
    },
    filteredWhitelist() {
      var self = this;
      var items = this.whitelistData;

      if (this.filters.target_type) {
        items = items.filter(function (item) {
          return item.target_type === self.filters.target_type;
        });
      }

      if (this.filters.status === 'active') {
        items = items.filter(function (item) { return item.is_active; });
      } else if (this.filters.status === 'expired') {
        items = items.filter(function (item) {
          return !item.is_active && item.end_date;
        });
      }

      if (this.searchQuery) {
        var search = this.searchQuery.toLowerCase();
        items = items.filter(function (item) {
          var name = item.target_name || '';
          var type = item.target_type || '';
          return name.toLowerCase().indexOf(search) !== -1 ||
              type.toLowerCase().indexOf(search) !== -1;
        });
      }

      return items;
    },
    displayedWhitelist() {
      var start = (this.pagination.currentPage - 1) * this.pagination.perPage;
      var end = start + this.pagination.perPage;
      return this.filteredWhitelist.slice(start, end);
    },
    totalItems() {
      return this.filteredWhitelist.length;
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    // ============ DATA ============
    async fetchData() {
      this.isPageLoading = true;
      try {
       let page= {
          number: this.filters.page,
              size: this.filters.per_page
        };
        var response = await this.$store.dispatch('biometricWhitelists/list', {
          // start_date: this.filters.start_date,
          // end_date: this.filters.end_date,
          search: this.filters.search,
          page: page,

        });
        this.whitelistData = response.list || [];
        this.total = this.$store.getters['biometricWhitelists/listTotal'] || 0;
      } catch (error) {
        console.error('Error fetching whitelist:', error);
        this.notifyError('Failed to load whitelist data');
      } finally {
        this.isPageLoading = false;
      }
    },
    async onTableFetch(state) {
      this.filters.search = state.search
      this.filters.per_page = state.perPage
      this.filters.page = state.page
      await this.fetchData();
    },
    // onTableFetch(state) {
    //   this.pagination.currentPage = state.page;
    //   this.pagination.perPage = state.perPage;
    //   this.searchQuery = state.search || '';
    // },

    onFilterChange() {
      this.pagination.currentPage = 1;
    },

    openCreateModal() {
      console.log('openCreateModal called');
      this.modal.item = null;
      this.modal.active = true;
    },

    openEditModal(item) {
      this.modal.item = item;
      this.modal.active = true;
    },

    onModalSuccess(payload) {
      this.notifySuccess(
          payload.isEdit
              ? 'Whitelist entry updated successfully'
              : 'Added to biometric whitelist successfully'
      );
      this.fetchData();
    },

    onModalError(error) {
      this.notifyException(error);
    },

    onModalClosed() {
      this.modal.item = null;
    },

    // ============ DELETE ============
    confirmDelete(item) {
      var self = this;
      Swal.fire({
        title: 'Remove from Whitelist?',
        text: 'Are you sure you want to remove "' + (item.target_name || 'this entry') + '" from the whitelist?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      }).then(async function (result) {
        if (result.value) {
          try {
            await self.$store.dispatch('biometricWhitelists/destroy', item.id);
            self.notifySuccess('Removed from whitelist successfully');
            await self.fetchData();
          } catch (error) {
            console.error('Error deleting whitelist:', error);
            self.notifyException(error);
          }
        }
      });
    },

    // ============ EXPORT ============
    exportData() {
      var self = this;
      var data = this.filteredWhitelist.map(function (item) {
        return {
          'Target Type': self.formatTargetType(item.target_type),
          'Target Name': item.target_name || 'N/A',
          'Start Date': self.formatDate(item.start_date),
          'End Date': item.end_date ? self.formatDate(item.end_date) : 'Permanent',
          'Status': item.is_active ? 'Active' : 'Expired',
          'Reason': item.reason || '',
          'Created By': item.creator ? self.getCreatorName(item.creator) : 'System',
          'Created At': self.formatDateTime(item.created_at)
        };
      });

      var csv = this.convertToCSV(data);
      var blob = new Blob([csv], { type: 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'biometric_whitelist_' + this.formatDateYMD(new Date()) + '.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    },

    convertToCSV(data) {
      if (!data || !data.length) return '';
      var headers = Object.keys(data[0]);
      var rows = data.map(function (obj) {
        return headers.map(function (header) {
          return JSON.stringify(obj[header] || '');
        }).join(',');
      });
      return [headers.join(',')].concat(rows).join('\n');
    },

    // ============ HELPERS ============
    getCreatorName(creator) {
      if (creator.model) {
        var firstName = creator.model.first_name || '';
        var lastName = creator.model.last_name || '';
        return (firstName + ' ' + lastName).trim() || creator.name;
      }
      return creator.name || 'N/A';
    },

    formatTargetType(type) {
      var types = {
        station: 'Station',
        workplace: 'Workplace',
        port: 'Port',
        department: 'Department',
        labourer: 'Labourer'
      };
      return types[type] || type;
    },

    getTargetTypeBadge(type) {
      var badges = {
        station: 'primary',
        workplace: 'success',
        port: 'info',
        department: 'warning',
        labourer: 'danger'
      };
      return badges[type] || 'default';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },

    formatDateYMD(date) {
      if (!date) return null;
      var d = new Date(date);
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    }
  }
};
</script>

<style lang="scss" scoped>
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

.reason-text {
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .reason-text {
    max-width: 120px;
  }
}
</style>